import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, Trash2, RefreshCw, Archive, CheckSquare, Square, Clock, Database } from 'lucide-react';
import { theme, cardStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  RETENTION — DPDP/NABH compliance — purge old logs + archive
//  dormant patients beyond the retention cut-off.
// ═══════════════════════════════════════════════════════════════════

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const RetentionPage = ({ API_BASE, currentUser }) => {
  const [report, setReport] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());

  const isAdmin = ['owner','admin','super_admin'].includes(currentUser?.role);
  const showMsg = (t, text) => { setMsg({ type: t, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API_BASE}/retention/report`),
        axios.post(`${API_BASE}/retention/preview-logs`),
      ]);
      setReport(r.data); setPreview(p.data);
    } catch (e) { showMsg('error', 'Load failed'); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const purgeNow = async () => {
    if (!window.confirm(`Old logs (${report.years}+ years) delete karein? Yeh permanent hai.`)) return;
    setWorking(true);
    try {
      const r = await axios.post(`${API_BASE}/retention/purge-logs`);
      showMsg('success', `✓ Purged ${r.data.totalDeleted} old log records`);
      load();
    } catch (e) { showMsg('error', 'Purge failed'); }
    finally { setWorking(false); }
  };

  const archiveSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`${selectedIds.size} inactive patients archive karein?`)) return;
    setWorking(true);
    try {
      const r = await axios.post(`${API_BASE}/retention/archive-patients`, { ids: Array.from(selectedIds) });
      showMsg('success', `✓ Archived ${r.data.archived} patient records`);
      setSelectedIds(new Set());
      load();
    } catch (e) { showMsg('error', 'Archive failed'); }
    finally { setWorking(false); }
  };

  const toggle = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleAll = () => {
    if (!report?.inactivePatients?.sample) return;
    if (selectedIds.size === report.inactivePatients.sample.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(report.inactivePatients.sample.map(p => p._id)));
  };

  return (
    <div>
      <div style={{ ...cardStyle, marginBottom: 16, background: `linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark})`, color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.4rem' }}><Shield size={22} style={{ verticalAlign: -4, marginRight: 6 }} /> Data Retention (DPDP Compliance)</h2>
            <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: 13 }}>
              {report ? `Retention: ${report.years} years · Cut-off: ${fmt(report.cutoff)}` : 'Loading…'} · auto-purge weekly Sunday 03:00 IST
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={load} style={headBtn}><RefreshCw size={14} style={{ marginRight: 4 }} /> Refresh</button>
          </div>
        </div>
      </div>

      {msg.text && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8,
          background: msg.type === 'error' ? '#fee' : '#efe',
          color: msg.type === 'error' ? theme.danger : theme.success,
          border: `1px solid ${msg.type === 'error' ? theme.danger : theme.success}44`,
          fontWeight: 600, fontSize: 13.5,
        }}>{msg.text}</div>
      )}

      {loading ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 40 }}>Loading…</div>
      ) : (
        <>
          {/* Logs auto-purge section */}
          <div style={{ ...cardStyle, padding: 16, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <Database size={18} color={theme.primary} />
              <h3 style={{ margin: 0, color: theme.navy, fontSize: 15 }}>Audit / Access Logs — safe auto-purge</h3>
            </div>
            <p style={{ fontSize: 13, color: theme.textMuted, margin: '0 0 10px' }}>
              Ye records safely delete kiye jaate hain (no patient data). Weekly cron automatic chalaata hai Sunday 03:00 IST pe.
            </p>
            {preview?.wouldDelete && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10, marginBottom: 10 }}>
                <StatPill label="Access Logs" value={preview.wouldDelete.accessLogs} />
                <StatPill label="Audit Logs"  value={preview.wouldDelete.auditLogs} />
                <StatPill label="Login History" value={preview.wouldDelete.loginHistory} />
              </div>
            )}
            {isAdmin && (
              <button onClick={purgeNow} disabled={working} style={{ ...btnStyle, marginTop: 4, background: theme.danger, opacity: working ? 0.6 : 1 }}>
                <Trash2 size={14} style={{ marginRight: 6 }} /> Purge Now
              </button>
            )}
          </div>

          {/* Patient retention review */}
          {report && (
            <div style={{ ...cardStyle, padding: 16, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <AlertTriangle size={18} color="#f59e0b" />
                <h3 style={{ margin: 0, color: theme.navy, fontSize: 15 }}>Inactive Patients — admin review required</h3>
              </div>
              <p style={{ fontSize: 13, color: theme.textMuted, margin: '0 0 12px' }}>
                Patients with no visit/treatment/bill activity for over <strong>{report.years} years</strong> qualify for archival under DPDP retention rules. <strong>Review karke approve karein — auto-delete nahi hota.</strong>
              </p>

              {report.inactivePatients.count === 0 ? (
                <div style={{ padding: 16, background: '#ecfdf5', borderRadius: 8, color: '#065f46', fontWeight: 600, fontSize: 13 }}>
                  ✓ Koi inactive patient nahi — clinic clean hai.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ fontSize: 13, color: theme.navy }}>
                      <strong>{report.inactivePatients.count}</strong> patients qualify (showing first {report.inactivePatients.sample.length})
                    </div>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={toggleAll} style={smallBtn}>
                          {selectedIds.size === report.inactivePatients.sample.length ? <CheckSquare size={12} /> : <Square size={12} />} Select all
                        </button>
                        <button onClick={archiveSelected} disabled={selectedIds.size === 0 || working}
                          style={{ ...smallBtn, background: '#f59e0b22', color: '#92400e', borderColor: '#f59e0b55', opacity: selectedIds.size === 0 ? 0.5 : 1 }}>
                          <Archive size={12} /> Archive {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ maxHeight: 400, overflowY: 'auto', border: `1px solid ${theme.borderLight}`, borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: theme.bg }}>
                        <tr>
                          <th style={th}></th>
                          <th style={th}>Patient Name</th>
                          <th style={th}>Phone</th>
                          <th style={th}>Last Activity</th>
                          <th style={th}>Registered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.inactivePatients.sample.map(p => (
                          <tr key={p._id} style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                            <td style={td}>
                              {isAdmin && (
                                <input type="checkbox" checked={selectedIds.has(p._id)} onChange={() => toggle(p._id)} />
                              )}
                            </td>
                            <td style={{ ...td, fontWeight: 600 }}>{p.fullName}</td>
                            <td style={td}>{p.phone || '—'}</td>
                            <td style={{ ...td, color: '#dc2626', fontWeight: 600 }}>{fmt(p.lastActivity)}</td>
                            <td style={td}>{fmt(p.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Old transactional records summary */}
          {report && (
            <div style={{ ...cardStyle, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Clock size={18} color={theme.primary} />
                <h3 style={{ margin: 0, color: theme.navy, fontSize: 15 }}>Old Transactional Records (retained — informational only)</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 10 }}>
                <StatPill label="Old Bills" value={report.oldRecords.bills} />
                <StatPill label="Old Appointments" value={report.oldRecords.appointments} />
                <StatPill label="Old Treatments" value={report.oldRecords.treatments} />
                <StatPill label="Old Prescriptions" value={report.oldRecords.prescriptions} />
              </div>
              <p style={{ fontSize: 12, color: theme.textMuted, marginTop: 10, fontStyle: 'italic' }}>
                Ye records retain kiye gaye hain kyunki bills / treatment history legally 7-10 years preserve karni padti hai. Manual purge sirf individual patient archive ke saath hoga.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const StatPill = ({ label, value }) => (
  <div style={{ padding: '10px 12px', background: theme.bg, borderRadius: 8 }}>
    <div style={{ fontSize: 10.5, color: theme.textMuted, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 800, color: theme.navy }}>{(value || 0).toLocaleString('en-IN')}</div>
  </div>
);

const headBtn = { ...btnStyle, marginTop: 0, background: 'rgba(255,255,255,0.18)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' };
const th = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, textAlign: 'left' };
const td = { padding: '8px 10px', color: theme.navy, verticalAlign: 'middle' };
const smallBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 5, border: `1px solid ${theme.border}`, background: 'white', color: theme.navy, cursor: 'pointer', fontSize: 11.5, fontWeight: 600 };

export default RetentionPage;
